import classNames from 'classnames';
import c from './display.module.scss';
import {
  Button,
  Dropdown,
  fontStyles,
  TextField,
  Toggle,
} from '@monash/portal-react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  AppIcons,
  formatUrl,
  getIcon,
  isUrl,
  PERSONALISATION_OPTIONS,
} from '@monash/portal-frontend-common';
import { getLabelForAppIcon, sortAppIconOptions } from './utils';
import { INLINE_ERROR_MSGS } from '../../../../constants/inline-error-messages';
import InlineErrorMessage from '../../../common/inline-error-message/InlineErrorMessage';
import LinkUrlInputs from './link-url-inputs/LinkUrlInputs';
import PersonalisationTagInput from './personalisation-tag-input/PersonalisationTagInput';
import {
  EMPTY_LINK_DATA,
  EMPTY_PERSONALISATION_TAG_DATA,
  LINK_TYPE_OPTIONS,
  LINK_TYPES,
  LINKS_INLINE_ERROR_MSGS,
  MAX_LINKS,
  MAX_RELEVANCE,
  MIN_LINKS,
  MIN_PERSONAS,
  MIN_RELEVANCE,
} from '../constants';

const Display = ({ disabled }) => {
  const {
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const linkType = useWatch({ name: 'linkType' });
  const isServiceOrExperience = [
    LINK_TYPES.SERVICE,
    LINK_TYPES.EXPERIENCE,
  ].includes(linkType);

  const {
    fields: links,
    append: appendLink,
    remove: removeLink,
  } = useFieldArray({
    name: 'links',
  });

  const {
    fields: personalisationTags,
    append: appendPersonalisationTag,
    remove: removePersonalisationTag,
  } = useFieldArray({
    name: 'personalisationTags',
  });

  const allPersonalisationTags =
    useWatch({ name: 'personalisationTags' }) ?? [];
  const selectedPersonalisationValues = allPersonalisationTags
    .map(({ value }) => value)
    .filter(Boolean);

  const getPersonalisationOptionsForIndex = (index) => {
    const currentValue = allPersonalisationTags[index]?.value;

    return PERSONALISATION_OPTIONS.filter(
      ({ value }) =>
        value === currentValue || !selectedPersonalisationValues.includes(value)
    );
  };

  const iconOptions = Object.keys(AppIcons)
    .map((iconKey) => {
      return {
        text: getLabelForAppIcon(iconKey),
        value: iconKey,
        label: <div className={c.appIconContainer}>{getIcon(iconKey)}</div>,
      };
    })
    .sort(sortAppIconOptions);

  return (
    <div className={c.container}>
      <div className={c.section}>
        <div className={classNames(fontStyles.label, c.label)}>Type</div>
        <Controller
          name="linkType"
          rules={{ required: LINKS_INLINE_ERROR_MSGS.TYPE }}
          render={({ field: { value, onChange } }) => {
            const toggleOptions = LINK_TYPE_OPTIONS.map(
              ({ label, value: storedValue }) => ({
                value: label,
                ariaLabel: label,
                storedValue,
              })
            );

            const selectedOption =
              toggleOptions.find((option) => option.storedValue === value) ??
              toggleOptions[0];

            return (
              <Toggle
                options={toggleOptions}
                selected={selectedOption}
                setSelected={(option) => onChange(option.storedValue)}
                ariaLabel="Link type"
                fullWidth
                mode="card"
              />
            );
          }}
        />
        {errors.linkType && (
          <InlineErrorMessage message={errors.linkType?.message} />
        )}
      </div>
      <div className={c.section}>
        <div className={classNames(fontStyles.label, c.label)}>Title</div>
        <Controller
          name="name"
          rules={{ required: INLINE_ERROR_MSGS.TITLE }}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              placeholder="Title"
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              multiline
              disabled={disabled}
              error={() => errors.name}
              errorMsg={errors.name?.message}
            />
          )}
        />
      </div>
      {linkType === LINK_TYPES.LINK && (
        <div className={c.section}>
          <div className={classNames(fontStyles.label, c.label)}>Icon</div>
          <Controller
            name="icon"
            rules={{
              required: LINKS_INLINE_ERROR_MSGS.ICON,
            }}
            render={({ field: { value, onChange, onBlur } }) => (
              <Dropdown
                data={iconOptions}
                value={value}
                onBlur={onBlur}
                onChange={(e) => {
                  onChange(e);
                  isSubmitted && trigger('icon'); // Manually validate b/c dropdown's onBlur doesn't validate on first blur
                }}
                disabled={disabled}
              />
            )}
          />
          {errors.icon && <InlineErrorMessage message={errors.icon?.message} />}
        </div>
      )}
      <div className={c.section}>
        <div className={classNames(fontStyles.label, c.label)}>Description</div>
        <Controller
          name="description"
          rules={{ required: INLINE_ERROR_MSGS.DESCRIPTION }}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              placeholder="Description"
              multiline
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled}
              error={() => errors.description}
              errorMsg={errors.description?.message}
            />
          )}
        />
      </div>
      {isServiceOrExperience && (
        <div className={c.section}>
          <div className={classNames(fontStyles.label, c.label)}>Persona</div>
          {personalisationTags.map((tag, index) => (
            <PersonalisationTagInput
              key={tag.id}
              index={index}
              removeTag={() => removePersonalisationTag(index)}
              disabled={disabled}
              canRemove={personalisationTags.length > MIN_PERSONAS}
              availableOptions={getPersonalisationOptionsForIndex(index)}
            />
          ))}
          <Button
            variant="text"
            size="small"
            onClick={() =>
              appendPersonalisationTag(EMPTY_PERSONALISATION_TAG_DATA)
            }
            className={c.addLinkButton}
            disabled={
              disabled ||
              personalisationTags.length >= PERSONALISATION_OPTIONS.length
            }
          >
            Add persona
          </Button>
        </div>
      )}
      <div className={c.section}>
        <div className={c.linksGrid}>
          <div className={classNames(fontStyles.label, c.label)}>Link text</div>
          <div className={classNames(fontStyles.label, c.label)}>Link URL</div>
          <div inert />

          {links.map((linkData, index) => (
            <LinkUrlInputs
              key={linkData.id}
              index={index}
              removeLinkUrl={() => removeLink(index)}
              disabled={disabled}
              canRemove={links.length > MIN_LINKS}
            />
          ))}
        </div>
        <Button
          variant="text"
          size="small"
          onClick={() => appendLink(EMPTY_LINK_DATA)}
          className={c.addLinkButton}
          disabled={disabled || links.length >= MAX_LINKS}
        >
          Add link
        </Button>
      </div>
      {isServiceOrExperience && (
        <>
          <div className={c.section}>
            <div className={classNames(fontStyles.label, c.label)}>
              Image URL
            </div>
            <Controller
              name="imageUrl"
              rules={{
                validate: (value) => {
                  const formattedUrl = formatUrl(value);
                  if (!isUrl(formattedUrl)) return LINKS_INLINE_ERROR_MSGS.URL;
                },
              }}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={disabled}
                />
              )}
            />
            {errors.imageUrl && (
              <InlineErrorMessage message={errors.imageUrl?.message} />
            )}
          </div>
          <div className={c.section}>
            <div className={classNames(fontStyles.label, c.label)}>
              Display label (optional)
            </div>
            <Controller
              name="displayLabel"
              render={({ field: { value, onChange, onBlur } }) => (
                <TextField
                  placeholder="Display label"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  disabled={disabled}
                />
              )}
            />
          </div>
        </>
      )}
      <div className={c.section}>
        <div className={classNames(fontStyles.label, c.label)}>
          Weighting (optional)
        </div>
        <Controller
          name="relevance"
          rules={{
            validate: (value) => {
              if (value === '' || value == null || Number.isNaN(value)) return;

              if (
                !Number.isInteger(value) ||
                value < MIN_RELEVANCE ||
                value > MAX_RELEVANCE
              ) {
                return LINKS_INLINE_ERROR_MSGS.RELEVANCE;
              }
            },
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              placeholder={`${MIN_RELEVANCE}-${MAX_RELEVANCE}`}
              value={value ?? ''}
              onChange={(e) => {
                const inputValue = e.target.value;
                onChange(inputValue === '' ? '' : parseInt(inputValue));
              }}
              onBlur={onBlur}
              type="number"
              disabled={disabled}
            />
          )}
        />
        {errors.relevance && (
          <InlineErrorMessage message={errors.relevance?.message} />
        )}
      </div>
    </div>
  );
};

export default Display;
